import React, { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Copy, Check, Share2, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const SITE_NAME = "Hidden Gem";
const SITE_TAGLINE = "Local food + wholesale finds. Hidden Gem.";

function siteOrigin() {
  if (typeof window === "undefined") return "https://yourhiddengem.com";
  // On production this is `https://yourhiddengem.com`. On preview it is the
  // preview URL. Either is a valid share target.
  return window.location.origin;
}

/**
 * ShareSheet — universal share-the-site dialog with a QR code.
 *
 * Tabs:
 *   - Hidden Gem (the homepage)
 *   - My profile (current user's public profile)
 *   - My shop (only for approved vendors)
 *
 * Each tab renders a QR code + the URL + Copy / Native-share / Download QR PNG.
 */
export default function ShareSheet({ open, onOpenChange }) {
  const { user } = useAuth();
  const isVendor = !!user?.is_vendor && user?.is_approved !== false && !user?.is_suspended;

  const tabs = useMemo(() => {
    const origin = siteOrigin();
    const items = [
      {
        key: "site",
        label: "Hidden Gem",
        url: `${origin}/`,
        shareTitle: SITE_NAME,
        shareText: SITE_TAGLINE,
      },
    ];
    if (user) {
      items.push({
        key: "profile",
        label: "My profile",
        url: `${origin}/u/${user.user_id}`,
        shareTitle: `${user.name} on Hidden Gem`,
        shareText: `Check out ${user.name}'s profile on Hidden Gem`,
      });
    }
    if (isVendor) {
      items.push({
        key: "shop",
        label: "My shop",
        url: `${origin}/vendor/${user.user_id}`,
        shareTitle: `${user.vendor_profile?.business_name || user.name} · Hidden Gem`,
        shareText: `Check out ${user.vendor_profile?.business_name || user.name} on Hidden Gem`,
      });
    }
    return items;
  }, [user, isVendor]);

  const [activeKey, setActiveKey] = useState("site");
  const active = tabs.find((t) => t.key === activeKey) || tabs[0];
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(active.url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const onNativeShare = async () => {
    if (!navigator.share) {
      onCopy();
      return;
    }
    try {
      await navigator.share({
        title: active.shareTitle,
        text: active.shareText,
        url: active.url,
      });
    } catch {
      // User cancelled — silent
    }
  };

  const onDownloadPng = () => {
    // Convert the rendered SVG into a 512×512 PNG and trigger a download.
    const svg = document.querySelector('[data-testid="share-qr-svg"] svg');
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(image, 0, 0, 512, 512);
      const png = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = png;
      a.download = `hidden-gem-${active.key}-qr.png`;
      a.click();
    };
    image.src = `data:image/svg+xml;base64,${svg64}`;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="bg-zinc-950 border-t border-white/10 text-white rounded-t-3xl p-0 max-h-[90dvh] overflow-y-auto"
        data-testid="share-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-white/10">
          <SheetTitle className="text-white text-xl font-black tracking-tighter inline-flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-pink-400" /> Share Hidden Gem
          </SheetTitle>
          <SheetDescription className="text-zinc-400 text-sm">
            Anyone with this QR code or link can open it.
          </SheetDescription>
        </SheetHeader>

        {tabs.length > 1 && (
          <div className="px-5 pt-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="share-tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveKey(t.key)}
                className={`shrink-0 rounded-full h-9 px-4 text-xs font-bold uppercase tracking-widest border transition-colors ${
                  active.key === t.key
                    ? "bg-pink-500/20 text-pink-200 border-pink-400/40"
                    : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"
                }`}
                data-testid={`share-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="px-5 py-6 flex flex-col items-center gap-4">
          {/* QR card — neon-bordered, white interior for max scanability */}
          <div
            className="relative p-4 rounded-3xl bg-white shadow-[0_0_40px_-12px_rgba(236,72,153,0.55)] border border-pink-400/40"
            data-testid="share-qr-svg"
          >
            <QRCodeSVG
              value={active.url}
              size={232}
              level="H"
              includeMargin={false}
              bgColor="#FFFFFF"
              fgColor="#0F0F12"
              imageSettings={{
                src: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><circle cx='12' cy='12' r='11' fill='%23EC4899'/><text x='12' y='17' font-family='Inter,Arial' font-weight='900' font-size='14' text-anchor='middle' fill='white'>HG</text></svg>",
                height: 44,
                width: 44,
                excavate: true,
              }}
            />
          </div>

          {/* URL pill */}
          <div className="w-full max-w-sm">
            <div className="rounded-full bg-white/5 border border-white/10 flex items-center" data-testid="share-url-row">
              <span className="px-4 py-2.5 text-sm text-zinc-200 truncate flex-1" data-testid="share-url-text">
                {active.url.replace(/^https?:\/\//, "")}
              </span>
              <button
                onClick={onCopy}
                className="mr-1 px-3 h-9 rounded-full bg-pink-500 hover:bg-pink-400 text-white text-xs font-bold inline-flex items-center gap-1.5"
                aria-label="Copy link"
                data-testid="share-copy-btn"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>

          {/* Action row */}
          <div className="flex gap-2 w-full max-w-sm">
            <Button
              onClick={onNativeShare}
              className="flex-1 h-11 rounded-full bg-gradient-to-r from-pink-500 to-cyan-400 hover:from-pink-400 hover:to-cyan-300 text-white font-bold text-sm shadow-[0_0_24px_-6px_rgba(236,72,153,0.6)]"
              data-testid="share-native-btn"
            >
              <Share2 className="w-4 h-4 mr-2" /> Share via…
            </Button>
            <Button
              onClick={onDownloadPng}
              className="h-11 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 font-bold text-sm px-4"
              data-testid="share-download-btn"
              aria-label="Download QR PNG"
            >
              <Download className="w-4 h-4" />
            </Button>
          </div>

          <p className="text-[10px] uppercase tracking-widest text-zinc-500 text-center max-w-xs">
            Point any phone camera at the QR code to open Hidden Gem instantly.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
